/**
 * degreeDaysDataService.ts
 *
 * Fetches PRISM HDD65/CDD65 annual data and California incorporated-city benchmark.
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export const CLIMATE_YEAR_MIN = 2000;
export const CLIMATE_YEAR_MAX = 2024;
export const LATEST_CLIMATE_YEAR = 2024;

export interface DegreeDayAnnualRow {
  geofips: string;
  location_name: string;
  year: number;
  mean_temperature_f: number | null;
  hdd65_actual: number;
  cdd65_actual: number;
  hdd65_normal: number;
  cdd65_normal: number;
  hdd65_anomaly: number;
  cdd65_anomaly: number;
}

export interface DegreeDayStatewideRow {
  year: number;
  mean_hdd65_actual: number;
  mean_cdd65_actual: number;
  mean_hdd65_normal: number;
  mean_cdd65_normal: number;
  mean_hdd65_anomaly: number;
  mean_cdd65_anomaly: number;
  mean_temperature_f: number | null;
  n_cities: number;
  complete_city_count: number;
}

export interface DegreeDayLocationContext {
  supported: boolean;
  geographyType: string | null;
  climateplansGeofips: string | null;
  prismGeofips: string | null;
  prismLocationName: string | null;
  isLegacyCdpCorrection: boolean;
  unsupportedReason?: string;
}

export interface DegreeDaySeries {
  city: DegreeDayAnnualRow[];
  statewide: DegreeDayStatewideRow[];
  normalHdd65: number | null;
  normalCdd65: number | null;
}

function padGeofips(value: number | string | null | undefined): string | null {
  if (value == null) return null;
  const s = String(value).trim().split('.')[0];
  const placefp = s.slice(-5).padStart(5, '0');
  return `06${placefp}`;
}

/** Clamp inventory year to available PRISM annual range. */
export function resolveClimateDisplayYear(requestedYear: number): {
  year: number;
  substituted: boolean;
} {
  if (requestedYear < CLIMATE_YEAR_MIN) {
    return { year: LATEST_CLIMATE_YEAR, substituted: true };
  }
  if (requestedYear > CLIMATE_YEAR_MAX) {
    return { year: LATEST_CLIMATE_YEAR, substituted: true };
  }
  return { year: requestedYear, substituted: false };
}

export async function resolveDegreeDayLocation(
  locationName: string
): Promise<DegreeDayLocationContext> {
  await supabaseLocationService.initialize();
  const geo = await supabaseLocationService.getGeofipsWithGeographyType(locationName);
  if (!geo) {
    return {
      supported: false,
      geographyType: null,
      climateplansGeofips: null,
      prismGeofips: null,
      prismLocationName: null,
      isLegacyCdpCorrection: false,
      unsupportedReason: 'Location not found.',
    };
  }
  const gt = geo.geography_type;
  if (gt !== 'City') {
    return {
      supported: false,
      geographyType: gt,
      climateplansGeofips: padGeofips(geo.geofips),
      prismGeofips: null,
      prismLocationName: null,
      isLegacyCdpCorrection: false,
      unsupportedReason:
        'Heating and cooling degree days are available for incorporated California cities only. County, regional, and statewide views are not yet supported for this dataset.',
    };
  }

  const climateplansGeofips = padGeofips(geo.geofips);
  if (!climateplansGeofips) {
    return {
      supported: false,
      geographyType: gt,
      climateplansGeofips: null,
      prismGeofips: null,
      prismLocationName: null,
      isLegacyCdpCorrection: false,
      unsupportedReason: 'Could not resolve location identifier.',
    };
  }

  const { data: crosswalk, error } = await supabaseClient
    .from('city_climate_geography_crosswalk')
    .select('prism_geofips,location_name,is_legacy_cdp_correction')
    .eq('climateplans_geofips', climateplansGeofips)
    .maybeSingle();

  if (error) {
    console.error('degreeDays crosswalk error:', error);
    return {
      supported: false,
      geographyType: gt,
      climateplansGeofips,
      prismGeofips: null,
      prismLocationName: null,
      isLegacyCdpCorrection: false,
      unsupportedReason: 'Climate geography crosswalk is unavailable.',
    };
  }

  if (!crosswalk?.prism_geofips) {
    return {
      supported: false,
      geographyType: gt,
      climateplansGeofips,
      prismGeofips: null,
      prismLocationName: null,
      isLegacyCdpCorrection: false,
      unsupportedReason:
        'This incorporated place is not yet linked in the ClimatePlans climate geography crosswalk.',
    };
  }

  return {
    supported: true,
    geographyType: gt,
    climateplansGeofips,
    prismGeofips: crosswalk.prism_geofips,
    prismLocationName: crosswalk.location_name,
    isLegacyCdpCorrection: Boolean(crosswalk.is_legacy_cdp_correction),
  };
}

export async function fetchDegreeDaySeries(prismGeofips: string): Promise<DegreeDaySeries> {
  const [cityRes, stateRes, normalRes] = await Promise.all([
    supabaseClient
      .from('city_climate_degree_days')
      .select(
        'geofips,location_name,year,mean_temperature_f,hdd65_actual,cdd65_actual,hdd65_normal,cdd65_normal,hdd65_anomaly,cdd65_anomaly'
      )
      .eq('geofips', prismGeofips)
      .eq('climate_source', 'PRISM')
      .gte('year', CLIMATE_YEAR_MIN)
      .lte('year', CLIMATE_YEAR_MAX)
      .order('year', { ascending: true }),
    supabaseClient
      .from('city_climate_degree_days_statewide')
      .select('*')
      .gte('year', CLIMATE_YEAR_MIN)
      .lte('year', CLIMATE_YEAR_MAX)
      .order('year', { ascending: true }),
    supabaseClient
      .from('city_climate_degree_days')
      .select('hdd65_normal,cdd65_normal')
      .eq('geofips', prismGeofips)
      .eq('climate_source', 'PRISM')
      .order('year', { ascending: false })
      .limit(1),
  ]);

  if (cityRes.error) throw cityRes.error;
  if (stateRes.error) throw stateRes.error;

  const city = (cityRes.data ?? []) as DegreeDayAnnualRow[];
  const statewide = (stateRes.data ?? []) as DegreeDayStatewideRow[];
  const normalRow = normalRes.data?.[0];

  return {
    city,
    statewide,
    normalHdd65: normalRow?.hdd65_normal ?? null,
    normalCdd65: normalRow?.cdd65_normal ?? null,
  };
}

export function getDegreeDayRowForYear(
  series: DegreeDaySeries,
  year: number
): { city: DegreeDayAnnualRow | null; statewide: DegreeDayStatewideRow | null } {
  return {
    city: series.city.find((r) => r.year === year) ?? null,
    statewide: series.statewide.find((r) => r.year === year) ?? null,
  };
}
